import { stringifyQuery } from '@emico/url'

// Copied from https://github.com/h2non/imaginary#params
export interface ImaginaryParams {
  /* width int - Width of image area to extract/resize */
  width?: number
  /* height int - Height of image area to extract/resize */
  height?: number
  /* top int - Top edge of area to extract. Example: 100 */
  top?: number
  /* left int - Left edge of area to extract. Example: 100 */
  left?: number
  /* areawidth int - Height area to extract. Example: 300 */
  areawidth?: number
  /* areaheight int - Width area to extract. Example: 300 */
  areaheight?: number
  /* quality int - JPEG image quality between 1-100. Defaults to 80 */
  quality?: number
  /* compression int - PNG compression level. Default: 6 */
  compression?: number
  /* rotate int - Image rotation angle. Must be multiple of 90. Example: 180 */
  rotate?: number
  /* factor int - Zoom factor level. Example: 2 */
  factor?: number
  /* margin int - Text area margin for watermark. Example: 50 */
  margin?: number
  /* dpi int - DPI value for watermark. Example: 150 */
  dpi?: number
  /* textwidth int - Text area width for watermark. Example: 200 */
  textwidth?: number
  /* opacity float - Opacity level for watermark text or watermark image. Default: 0.2 */
  opacity?: number
  /* flip bool - Transform the resultant image with flip operation. Default: false */
  flip?: boolean
  /* flop bool - Transform the resultant image with flop operation. Default: false */
  flop?: boolean
  /* force bool - Force image transformation size. Default: false */
  force?: boolean
  /* nocrop bool - Disable crop transformation. Defaults depend on the operation */
  nocrop?: boolean
  /* noreplicate bool - Disable text replication in watermark. Defaults to false */
  noreplicate?: boolean
  /* norotation bool - Disable auto rotation based on EXIF orientation. Defaults to false */
  norotation?: boolean
  /* noprofile bool - Disable adding ICC profile metadata. Defaults to false */
  noprofile?: boolean
  /* stripmeta bool - Remove original image metadata, such as EXIF metadata. Defaults to false */
  stripmeta?: boolean
  /* text string - Watermark text content. Example: copyright (c) 2189 */
  text?: string
  /* font string - Watermark text font type and format. Example: sans bold 12 */
  font?: string
  /* color string - Watermark text RGB decimal base color. Example: 255,200,150 */
  color?: string
  /* image string - Watermark image URL pointing to the remote HTTP server. */
  image?: string
  /* type string - Specify the image format to output. Possible values are: jpeg, png, webp and auto. auto will use the preferred format requested by the client in the HTTP Accept header. A client can provide multiple comma-separated choices in Accept with the best being the one picked. */
  type?: 'webp' | 'jpeg' | 'png' | 'auto'
  /* gravity string - Define the crop operation gravity. Supported values are: north, south, centre, west, east and smart. Defaults to centre. */
  gravity?: string
  /* url string - Fetch the image from a remote HTTP server. In order to use this you must pass the -enable-url-source flag. */
  url: string
  /* colorspace string - Use a custom color space for the output image. Allowed values are: srgb or bw (black&white) */
  colorspace?: string
  /* field string - Custom image form field name if using multipart/form. Defaults to: file */
  field?: string
  /* extend string - Extend represents the image extend mode used when the edges of an image are extended. Allowed values are: black, copy, mirror, white and background. If background value is specified, you can define the desired extend RGB color via background param, such as ?extend=background&background=250,20,10. For more info, see libvips docs. */
  extend?: string
  /* background string - Background RGB decimal base color to use when flattening transparent PNGs. Example: 255,200,150 */
  background?: string
  /* sigma float - Size of the gaussian mask to use when blurring an image. Example: 15.0 */
  sigma?: number
  /* minampl float - Minimum amplitude of the gaussian filter to use when blurring an image. Default: Example: 0.5 */
  minampl?: number
  /* action string - The action to run */
  action: ImaginaryActions
}

export enum ImaginaryActions {
  CROP = 'crop',
  BLUR = 'blur',
  WATERMARKIMAGE = 'watermarkimage',
  WATERMARK = 'watermark',
  ZOOM = 'zoom',
  THUMBNAIL = 'thumbnail',
  CONVERT = 'convert',
  FLOP = 'flop',
  FLIP = 'flip',
  ROTATE = 'rotate',
  EXTRACT = 'extract',
  ENLARGE = 'enlarge',
  RESIZE = 'resize',
  SMARTCROP = 'smartcrop',
}

const imaginaryUrl = process.env.REACT_APP_IMAGINARY_URL
const quality = process.env.REACT_APP_IMAGINARY_QUALITY

function getImaginaryUrl({ action, url, ...options }: ImaginaryParams) {
  if (url.includes('//localhost')) {
    return url
  }
  if (!imaginaryUrl) {
    throw new Error(
      'Imaginary URL is not set. Add REACT_APP_IMAGINARY_URL to your .env file.',
    )
  }

  const optionsQuery = Object.entries(options).reduce(
    (obj: { [key: string]: string | number | undefined }, [key, value]) => {
      obj[key] = typeof value === 'boolean' ? String(value) : value
      return obj
    },
    { type: 'auto', stripmeta: 'true', url, quality },
  )

  return `${imaginaryUrl}/${action}?${stringifyQuery(optionsQuery)}`
}

export default getImaginaryUrl
